import * as THREE from 'three'

import { AIRCRAFT, COCKPIT } from '../config/constants.js'
import { DEG, clamp, remap } from '../util/units.js'
import {
  aoaIndicator,
  airspeedIndicator,
  altimeter,
  attitudeIndicator,
  headingIndicator,
  verticalSpeed,
} from './gauges.js'
import { panelTexture, structureTexture } from './cockpitTextures.js'

/**
 * The F-14 front cockpit, procedurally built.
 *
 * Scope is deliberate: every control that matters for a CASE 1 recovery is
 * here and clickable, and nothing else is. A full Tomcat pit has several
 * hundred switches, most of which do nothing for the twenty seconds this
 * trainer is about.
 *
 * Anything clickable carries `userData.action`, which is all the interaction
 * layer needs to know. Handles animate from the systems state rather than from
 * the click, so a keyboard press and a mouse click produce identical motion.
 */

const CLICK = (obj, action, label) => {
  obj.userData.action = action
  obj.userData.label = label
  return obj
}

/** Pilot design eye, model local frame. Must match EYE_LOCAL in F14Model.js. */
export const EYE_LOCAL_M = { x: 0, y: 1.02, z: -7.05 }

/**
 * The cockpit shell, as data rather than inline geometry.
 *
 * This exists as an exported table so `npm test` can cast rays from the design
 * eye and prove nothing solid sits in the forward field of view. The first
 * build of this cockpit used a single BoxGeometry for the tub — which has a
 * front face, with the camera inside it — and the result was a screen full of
 * the inside of a box and no world at all. That failure is invisible to every
 * other kind of check, so it gets its own test.
 *
 * `[size, position]`, both in metres, model local frame (-Z forward, +Y up).
 */
export const STRUCTURE_PANELS = [
  { name: 'floor', size: [1.12, 0.06, 2.7], pos: [0, 0.06, -6.9] },
  // Canopy rails, deliberately BELOW eye level (top at 0.78 against an eye at
  // 1.02). A Tomcat has a bubble canopy and genuinely good visibility; walls
  // that reach past the pilot's eye turn it into a letterbox.
  { name: 'left rail', size: [0.06, 0.72, 2.7], pos: [-0.56, 0.42, -6.9] },
  { name: 'right rail', size: [0.06, 0.72, 2.7], pos: [0.56, 0.42, -6.9] },
  { name: 'rear bulkhead', size: [1.12, 1.05, 0.06], pos: [0, 0.56, -5.58] },
  // Forward shroud sits BELOW the instrument panel only, so it hides the
  // fuselage interior without blocking the view over the coaming.
  { name: 'forward shroud', size: [1.12, 0.42, 0.06], pos: [0, 0.2, -8.12] },
  { name: 'glareshield', size: [1.0, 0.08, 0.34], pos: [0, 0.86, -7.86] },
  { name: 'instrument panel', size: [0.8, 0.48, 0.05], pos: [0, 0.62, -8.0] },
]

/**
 * The window: within this cone, nothing solid may sit between the pilot's eye
 * and the world for at least `clearRangeM`.
 *
 * Six degrees of depression is a tight but honest coaming — you look just over
 * it, which is the picture from a Tomcat front seat, and it is enough to see
 * the deck from the groove (the ramp sits about 3.5 degrees below the nose on
 * glideslope).
 */
export const SIGHTLINE = {
  maxDepressionDeg: 6,
  maxElevationDeg: 25,
  halfAzimuthDeg: 35,
  clearRangeM: 5,
}

export class Cockpit {
  constructor() {
    this.group = new THREE.Group()
    this.group.name = 'Cockpit'

    /** @type {THREE.Object3D[]} meshes the raycaster tests against */
    this.clickables = []
    this._materials = []
    this._gaugeAccumulator = 0

    /** Lights, kept separate so they can survive when the geometry does not. */
    this.lights = []

    this._buildLighting()
    this._buildStructure()
    this._buildPanel()
    this._buildIndexer()
    this._buildLeftConsole()
    this._buildRightConsole()
    this._buildStick()
    this._isolateClickableMaterials()
  }

  /**
   * Give every clickable its own material.
   *
   * The interaction layer highlights a hovered control by writing to
   * `material.emissive`. Handles share materials for the obvious reason — they
   * are all the same bare metal — so without this, hovering the gear knob also
   * lights up the throttle, the flap lever and the sweep lever.
   */
  _isolateClickableMaterials() {
    for (const obj of this.clickables) {
      if (!obj.material || Array.isArray(obj.material)) continue
      obj.material = obj.material.clone()
      this._materials.push(obj.material)
    }
  }

  _mat(opts) {
    const m = new THREE.MeshStandardMaterial(opts)
    this._materials.push(m)
    return m
  }

  /* ------------------------------------------------------------------ */

  _buildStructure() {
    this.structureMap = structureTexture()
    const shellMat = this._mat({
      map: this.structureMap,
      color: 0xb9c2ca,
      roughness: 0.9,
      metalness: 0.1,
    })

    // Built from STRUCTURE_PANELS above — separate panels, never a single box,
    // because a box has a front face and the camera sits inside it.
    const darkMat = this._mat({ color: 0x14181a, roughness: 0.98 })
    for (const p of STRUCTURE_PANELS) {
      // The instrument panel is built with its instruments in _buildPanel.
      if (p.name === 'instrument panel') continue
      const m = new THREE.Mesh(
        new THREE.BoxGeometry(p.size[0], p.size[1], p.size[2]),
        p.name === 'glareshield' ? darkMat : shellMat,
      )
      m.position.set(p.pos[0], p.pos[1], p.pos[2])
      this.group.add(m)
    }

    // The canopy bow is opt-in. Measured from the design eye, a procedural one
    // is 63 degrees of solid tube across the middle of the view — two thirds
    // of the screen width. On the real jet the same structure carries rivets,
    // wear, reflections and depth, and the eye reads it as structure and looks
    // past it. Without those cues it is just a bar in the way.
    if (COCKPIT.style === 'framed') this._buildCanopyFrame()
    this._buildHUD()
  }

  /**
   * Light the inside of the cockpit.
   *
   * The scene's sun is outside and the cockpit is a box; with only a
   * directional light and a weak hemisphere term, every interior surface
   * renders as near-black silhouette no matter what colour it is. That, more
   * than any modelling choice, is why the early cockpits looked like cardboard
   * cut-outs.
   *
   * Point lights with a short falloff distance, so the carrier a kilometre
   * away is not affected. They live on the cockpit group, which is hidden
   * outside the cockpit view, and three skips lights under an invisible
   * parent — so they cost nothing in the other five views.
   */
  _buildLighting() {
    // Skylight coming down through the canopy.
    const sky = new THREE.PointLight(0xd6e6f2, 2.6, 4.5, 2)
    sky.position.set(0, 1.55, -7.1)
    this.group.add(sky)
    this.lights.push(sky)

    // Bounce off the panel and consoles, from below and behind the eye.
    const bounce = new THREE.PointLight(0xa8bccd, 1.1, 3.2, 2)
    bounce.position.set(0, 0.75, -7.25)
    this.group.add(bounce)
    this.lights.push(bounce)

    // A little warmth on the forward panel so the instruments read.
    const panelLamp = new THREE.PointLight(0xe6ddc8, 0.9, 2.2, 2)
    panelLamp.position.set(0, 1.0, -7.7)
    this.group.add(panelLamp)
    this.lights.push(panelLamp)
  }

  /**
   * Hide the procedural cockpit's geometry but leave its lights working.
   *
   * Used when the downloaded model supplies the cockpit interior. Those three
   * point lights are positioned inside the canopy at the pilot's shoulders and
   * over the panel, and they are the difference between an interior you can
   * read and a black hole — the sun is outside the fuselage and the model's
   * own cockpit gets almost nothing from it. Hiding the whole group to get rid
   * of the primitives takes the lights with it, because three.js skips lights
   * under an invisible parent.
   */
  setStructureVisible(visible) {
    this.group.visible = true
    for (const child of this.group.children) {
      if (child.isLight) continue
      child.visible = visible
    }
    this._structureHidden = !visible
  }

  /**
   * The canopy bow and windscreen frame.
   *
   * Shaped from the real thing rather than from a circle: the F-14's bow is a
   * wide, slightly flattened arch whose legs run down past the pilot's
   * shoulders to the canopy rails, with a riveted cross-member across the top
   * of the windscreen and two diagonal braces down to the coaming corners. It
   * is the single most recognisable thing about the view out of a Tomcat, and
   * a plain semicircular tube reads as a croquet hoop instead.
   */
  _buildCanopyFrame() {
    const frameMat = this._mat({ color: 0x232a33, roughness: 0.72, metalness: 0.22 })

    const bow = new THREE.Group()
    bow.position.set(0, 0.62, -8.22)
    this.group.add(bow)

    // Wider than tall, which is what flattens the arch.
    const arch = new THREE.Mesh(new THREE.TorusGeometry(0.5, 0.028, 8, 40, Math.PI), frameMat)
    arch.scale.set(1.45, 1.12, 1)
    bow.add(arch)

    // Legs continuing down to the rails, so the arch does not simply stop in
    // mid air where the torus ends.
    for (const side of [-1, 1]) {
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.032, 0.34, 8), frameMat)
      leg.position.set(side * 0.725, -0.17, 0)
      bow.add(leg)
    }

    // Cross-member over the top of the windscreen.
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.05, 0.09), frameMat)
    bar.position.set(0, 0.46, 0.1)
    bow.add(bar)

    // Diagonal braces from the cross-member down to the coaming corners.
    for (const side of [-1, 1]) {
      const brace = new THREE.Mesh(new THREE.BoxGeometry(0.028, 0.36, 0.028), frameMat)
      brace.position.set(side * 0.23, 0.24, 0.14)
      brace.rotation.z = side * 0.42
      brace.rotation.x = -0.22
      bow.add(brace)
    }
  }

  /**
   * The AN/AVG-12 HUD: a circular combiner in a ring mount above the panel.
   * The symbology itself is drawn on the 2D overlay; this is the physical
   * object it appears to float behind, and on the real jet it sits squarely in
   * the line of sight, which is the point of a head-up display.
   */
  _buildHUD() {
    const frameMat = this._mat({ color: 0x14181c, roughness: 0.55, metalness: 0.45 })

    const hud = new THREE.Group()
    hud.position.set(0, 1.0, -7.98)
    this.group.add(hud)

    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.085, 0.011, 8, 28), frameMat)
    hud.add(ring)

    const glass = new THREE.Mesh(
      new THREE.CircleGeometry(0.082, 28),
      new THREE.MeshPhysicalMaterial({
        color: 0xbfe9d4,
        transparent: true,
        opacity: 0.06,
        roughness: 0.04,
        metalness: 0,
        side: THREE.DoubleSide,
      }),
    )
    hud.add(glass)

    // Mount and the projector body below it.
    const stalk = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.09, 0.04), frameMat)
    stalk.position.set(0, -0.115, 0.01)
    hud.add(stalk)

    const body = new THREE.Mesh(new THREE.BoxGeometry(0.15, 0.07, 0.13), frameMat)
    body.position.set(0, -0.18, 0.03)
    hud.add(body)
  }

  _buildPanel() {
    // Light grey with black instrument bezels, as on the jet. The panel was
    // near-black before, which made the whole forward view read as one dark
    // mass with a few glowing discs floating in it.
    this.panelMap = panelTexture()
    const panelMat = this._mat({
      map: this.panelMap,
      color: 0xffffff,
      roughness: 0.9,
      metalness: 0.05,
    })
    const panel = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.48, 0.05), panelMat)
    panel.position.set(0, 0.62, -8.0)
    panel.rotation.x = 0.28
    this.group.add(panel)
    this.panel = panel

    const bezelMat = this._mat({ color: 0x121517, roughness: 0.8, metalness: 0.2 })

    this.gauges = {
      aoa: aoaIndicator(),
      adi: attitudeIndicator(),
      alt: altimeter(),
      ias: airspeedIndicator(),
      hsi: headingIndicator(),
      vvi: verticalSpeed(),
    }

    const layout = [
      ['aoa', -0.235, 0.105],
      ['adi', 0.0, 0.105],
      ['alt', 0.235, 0.105],
      ['ias', -0.235, -0.11],
      ['hsi', 0.0, -0.11],
      ['vvi', 0.235, -0.11],
    ]

    for (const [key, u, v] of layout) {
      const g = this.gauges[key]
      g.mesh.scale.setScalar(0.2)
      g.mesh.position.set(u, v, 0.032)
      panel.add(g.mesh)

      // Bezel behind each dial so the instruments read as mounted in the
      // panel rather than printed on it.
      const bezel = new THREE.Mesh(new THREE.TorusGeometry(0.106, 0.011, 6, 22), bezelMat)
      bezel.position.set(u, v, 0.028)
      panel.add(bezel)
    }

    // Secondary instruments and switch blocks. Non-functional, but a real
    // Tomcat panel is dense and an empty one looks like a prop.
    for (const [u, v, r] of [
      [-0.35, 0.2, 0.03],
      [0.35, 0.2, 0.03],
      [-0.35, -0.19, 0.026],
      [0.35, -0.19, 0.026],
      [0.0, 0.215, 0.024],
    ]) {
      const dial = new THREE.Mesh(new THREE.CircleGeometry(r, 18), bezelMat)
      dial.position.set(u, v, 0.028)
      panel.add(dial)
      const rim = new THREE.Mesh(new THREE.TorusGeometry(r + 0.006, 0.006, 6, 18), bezelMat)
      rim.position.set(u, v, 0.028)
      panel.add(rim)
    }

    // Switch strip along the bottom of the panel.
    for (let i = 0; i < 9; i++) {
      const sw = new THREE.Mesh(
        new THREE.BoxGeometry(0.018, 0.026, 0.016),
        i % 3 === 0 ? this._mat({ color: 0x8d2a22, roughness: 0.7 }) : bezelMat,
      )
      sw.position.set(-0.2 + i * 0.05, -0.215, 0.03)
      panel.add(sw)
    }

    // Landing gear position indicator: three lights above the panel.
    this.gearLights = []
    for (let i = 0; i < 3; i++) {
      const l = new THREE.Mesh(
        new THREE.CircleGeometry(0.016, 12),
        new THREE.MeshBasicMaterial({ color: 0x203030, toneMapped: false }),
      )
      l.position.set(-0.31 + i * 0.028, 0.2, 0.028)
      panel.add(l)
      this.gearLights.push(l)
    }

    // Hook light.
    this.hookLight = new THREE.Mesh(
      new THREE.CircleGeometry(0.018, 12),
      new THREE.MeshBasicMaterial({ color: 0x2a2418, toneMapped: false }),
    )
    this.hookLight.position.set(0.3, 0.2, 0.028)
    panel.add(this.hookLight)
  }

  /**
   * The AoA indexer: the three lights on the left of the glareshield that tell
   * you fast, slow or on-speed without looking inside the cockpit. In the
   * groove this is the only instrument you actually use.
   */
  _buildIndexer() {
    // Sized from the real thing: about 5 cm across, on the glareshield roughly
    // 75 cm from the eye. The first version was 8.5 x 16 cm and subtended 6 x
    // 12 degrees — a black slab covering an eighth of the screen.
    const box = new THREE.Mesh(
      new THREE.BoxGeometry(0.048, 0.082, 0.03),
      this._mat({ color: 0x0d1012, roughness: 0.9 }),
    )
    box.position.set(-0.215, 1.0, -7.8)
    this.group.add(box)

    const mk = (shape, color, y) => {
      const m = new THREE.Mesh(shape, new THREE.MeshBasicMaterial({ color, toneMapped: false }))
      m.position.set(0, y, 0.017)
      box.add(m)
      return m
    }

    // Chevron pointing down = fast (lower the nose). Doughnut = on speed.
    // Chevron pointing up = slow.
    const chevron = (up) => {
      const s = new THREE.Shape()
      const h = 0.010
      const w = 0.015
      if (up) {
        s.moveTo(-w, -h)
        s.lineTo(0, h)
        s.lineTo(w, -h)
        s.lineTo(w * 0.55, -h)
        s.lineTo(0, h * 0.35)
        s.lineTo(-w * 0.55, -h)
      } else {
        s.moveTo(-w, h)
        s.lineTo(0, -h)
        s.lineTo(w, h)
        s.lineTo(w * 0.55, h)
        s.lineTo(0, -h * 0.35)
        s.lineTo(-w * 0.55, h)
      }
      s.closePath()
      return new THREE.ShapeGeometry(s)
    }

    this.indexerSlow = mk(chevron(true), 0x22ff55, 0.027)
    this.indexerOnSpeed = mk(new THREE.RingGeometry(0.008, 0.013, 20), 0xffb02e, 0)
    this.indexerFast = mk(chevron(false), 0xff3b30, -0.027)
    this.indexerLights = [this.indexerSlow, this.indexerOnSpeed, this.indexerFast]
  }

  _buildLeftConsole() {
    const consoleMat = this._mat({ color: 0x1a1f22, roughness: 0.95 })
    const handleMat = this._mat({ color: 0x8f959a, roughness: 0.4, metalness: 0.6 })
    const warnMat = this._mat({ color: 0xb8342b, roughness: 0.5 })

    const console_ = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.1, 1.0), consoleMat)
    console_.position.set(-0.42, 0.5, -7.3)
    this.group.add(console_)

    // ---- throttle quadrant: two levers, dragged or keyed ---------------
    this.throttleGroup = new THREE.Group()
    this.throttleGroup.position.set(-0.42, 0.56, -7.05)
    this.group.add(this.throttleGroup)

    this.throttleLevers = []
    for (const [i, x] of [-0.035, 0.035].entries()) {
      const lever = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.13, 0.05), handleMat)
      lever.position.set(x, 0.06, 0)
      this.throttleGroup.add(lever)
      this.throttleLevers.push(lever)
      this.clickables.push(CLICK(lever, i === 0 ? 'throttle-down' : 'throttle-up', 'THROTTLE'))
    }
    const grip = new THREE.Mesh(
      new THREE.BoxGeometry(0.14, 0.05, 0.07),
      this._mat({ color: 0x24282b, roughness: 0.85 }),
    )
    grip.position.set(0, 0.14, 0)
    this.throttleGroup.add(grip)

    // ---- landing gear handle -------------------------------------------
    this.gearHandleGroup = new THREE.Group()
    this.gearHandleGroup.position.set(-0.31, 0.7, -7.92)
    this.group.add(this.gearHandleGroup)

    const gearStem = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.13, 0.022), handleMat)
    gearStem.position.y = -0.065
    this.gearHandleGroup.add(gearStem)
    const gearKnob = new THREE.Mesh(new THREE.SphereGeometry(0.032, 12, 10), handleMat)
    this.gearHandleGroup.add(gearKnob)
    this.clickables.push(CLICK(gearKnob, 'gear', 'LANDING GEAR'))

    // ---- flap handle ----------------------------------------------------
    this.flapHandleGroup = new THREE.Group()
    this.flapHandleGroup.position.set(-0.42, 0.62, -7.55)
    this.group.add(this.flapHandleGroup)
    const flapLever = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.11, 0.03), handleMat)
    flapLever.position.y = 0.05
    this.flapHandleGroup.add(flapLever)
    this.clickables.push(CLICK(flapLever, 'flaps', 'FLAPS'))

    // ---- wing sweep lever ----------------------------------------------
    this.sweepHandleGroup = new THREE.Group()
    this.sweepHandleGroup.position.set(-0.42, 0.62, -7.75)
    this.group.add(this.sweepHandleGroup)
    const sweepLever = new THREE.Mesh(new THREE.BoxGeometry(0.028, 0.1, 0.028), handleMat)
    sweepLever.position.y = 0.05
    this.sweepHandleGroup.add(sweepLever)
    this.clickables.push(CLICK(sweepLever, 'sweep-mode', 'WING SWEEP AUTO/MAN'))

    // ---- speedbrake switch ---------------------------------------------
    const sbSwitch = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.022, 0.05), warnMat)
    sbSwitch.position.set(-0.42, 0.56, -6.85)
    this.group.add(sbSwitch)
    this.clickables.push(CLICK(sbSwitch, 'speedbrake-toggle', 'SPEEDBRAKE'))
    this.speedbrakeSwitch = sbSwitch
  }

  _buildRightConsole() {
    const consoleMat = this._mat({ color: 0x1a1f22, roughness: 0.95 })
    const handleMat = this._mat({ color: 0x8f959a, roughness: 0.4, metalness: 0.6 })

    const console_ = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.1, 1.0), consoleMat)
    console_.position.set(0.42, 0.5, -7.3)
    this.group.add(console_)

    // ---- tailhook handle ------------------------------------------------
    this.hookHandleGroup = new THREE.Group()
    this.hookHandleGroup.position.set(0.33, 0.7, -7.92)
    this.group.add(this.hookHandleGroup)
    const hookStem = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.12, 0.022), handleMat)
    hookStem.position.y = -0.06
    this.hookHandleGroup.add(hookStem)
    const hookKnob = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.03, 0.04, 12),
      this._mat({ color: 0xd8b23a, roughness: 0.5, metalness: 0.4 }),
    )
    this.hookHandleGroup.add(hookKnob)
    this.clickables.push(CLICK(hookKnob, 'hook', 'TAILHOOK'))

    // ---- approach power compensator ------------------------------------
    const apc = new THREE.Mesh(
      new THREE.BoxGeometry(0.05, 0.024, 0.05),
      this._mat({ color: 0x2c6b4a, roughness: 0.6 }),
    )
    apc.position.set(0.42, 0.56, -7.05)
    this.group.add(apc)
    this.clickables.push(CLICK(apc, 'apc', 'APPROACH POWER COMP'))
    this.apcSwitch = apc

    // ---- DLC thumbwheel readout ----------------------------------------
    this.dlcWheel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.028, 0.028, 0.02, 14),
      handleMat,
    )
    this.dlcWheel.rotation.z = Math.PI / 2
    this.dlcWheel.position.set(0.42, 0.58, -6.85)
    this.group.add(this.dlcWheel)
    this.clickables.push(CLICK(this.dlcWheel, 'dlc-toggle', 'DIRECT LIFT CONTROL'))
  }

  _buildStick() {
    this.stickPivot = new THREE.Group()
    this.stickPivot.position.set(0, 0.28, -6.55)
    this.group.add(this.stickPivot)

    const shaft = new THREE.Mesh(
      new THREE.CylinderGeometry(0.017, 0.022, 0.34, 10),
      this._mat({ color: 0x2a2f33, roughness: 0.8 }),
    )
    shaft.position.y = 0.17
    this.stickPivot.add(shaft)

    const grip = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.03, 0.09, 4, 10),
      this._mat({ color: 0x15181a, roughness: 0.95 }),
    )
    grip.position.y = 0.38
    this.stickPivot.add(grip)
  }

  /* ------------------------------------------------------------------ */

  /**
   * @param {object} ctx
   * @param {import('../rails/RailsFlight.js').RailState} ctx.state
   * @param {object} ctx.systems
   * @param {object} ctx.inputs   raw stick/throttle commands, -1..1
   * @param {number} ctx.dt
   * @param {number} [ctx.bearingToShipDeg]
   */
  update(ctx) {
    const { state, systems, inputs, dt } = ctx

    // ---- instruments, redrawn at 20 Hz ---------------------------------
    this._gaugeAccumulator += dt
    if (this._gaugeAccumulator >= 0.05) {
      this._gaugeAccumulator = 0
      const s = {
        kcas: state.kcas,
        altFt: state.altFt,
        vsFpm: state.vsFpm,
        aoaUnits: state.aoaUnits,
        pitchDeg: state.pitchDeg,
        rollDeg: state.rollDeg,
        headingDeg: state.headingDeg,
        bearingToShipDeg: ctx.bearingToShipDeg,
      }
      for (const g of Object.values(this.gauges)) g.draw(s)
    }

    // ---- AoA indexer ----------------------------------------------------
    const err = state.aoaUnits - AIRCRAFT.ONSPEED_AOA_UNITS
    const live = state.gearNorm > 0.5
    const onSpeed = Math.abs(err) <= AIRCRAFT.AOA_DONUT_BAND
    this.indexerOnSpeed.visible = live && onSpeed
    // Slow means high AoA: the up chevron tells you to add power.
    this.indexerSlow.visible = live && err > AIRCRAFT.AOA_DONUT_BAND * 0.6
    this.indexerFast.visible = live && err < -AIRCRAFT.AOA_DONUT_BAND * 0.6

    // ---- gear and hook lights -------------------------------------------
    const gearDown = state.gearNorm > 0.98
    const gearMoving = state.gearNorm > 0.02 && state.gearNorm < 0.98
    for (const l of this.gearLights) {
      l.material.color.setHex(gearDown ? 0x2fe36a : gearMoving ? 0xffb02e : 0x203030)
    }
    this.hookLight.material.color.setHex(state.hookNorm > 0.9 ? 0x2fe36a : 0x2a2418)

    // ---- handles animate from system state, not from the click ----------
    this.gearHandleGroup.position.y = 0.7 - (systems?.gearDown ? 0.06 : 0)
    this.flapHandleGroup.rotation.x = remap(state.flapNorm, 0, 1, -0.5, 0.5)
    this.hookHandleGroup.position.y = 0.7 - (systems?.hookDown ? 0.055 : 0)
    this.sweepHandleGroup.rotation.x = remap(state.sweepNorm, 0, 1, -0.4, 0.4)
    this.dlcWheel.rotation.x = state.dlcNorm * 2.4

    this.speedbrakeSwitch.material.color.setHex(state.speedbrakeNorm > 0.05 ? 0xff5a4a : 0xb8342b)
    this.apcSwitch.material.color.setHex(systems?.apcEngaged ? 0x35d17a : 0x2c6b4a)

    // ---- throttle levers -------------------------------------------------
    // Forward is more power, so the lever travels toward -Z as it advances.
    const tz = remap(clamp(systems?.throttleLever ?? 0, 0, 1), 0, 1, 0.12, -0.12)
    for (const child of this.throttleGroup.children) child.position.z = tz

    // ---- stick position follows the actual commands ----------------------
    this.stickPivot.rotation.x = clamp(-(inputs?.pitch ?? 0), -1, 1) * 12 * DEG
    this.stickPivot.rotation.z = clamp(-(inputs?.roll ?? 0), -1, 1) * 12 * DEG
  }

  dispose() {
    this.panelMap?.dispose()
    this.structureMap?.dispose()
    for (const g of Object.values(this.gauges ?? {})) g.dispose()
    this.group.traverse((o) => {
      if (o.geometry) o.geometry.dispose()
    })
    this._materials.forEach((m) => m.dispose())
  }
}
